import ArticleAmpPage from "components/article-amp-page";
import ArticleContent from "components/the-buzz-theme/article/article-content";
import ArticlePage from "components/the-buzz-theme/pages/article-page";
import { Post } from "interfaces/wordpress";
import {
  CMS_NAME,
  getAmpIframeFromSrc,
  getAmpVideoFromId,
  getEmbedAmpInstagramPostFromPostId,
  getEmbedAmpYoutubePostFromPostId,
  getEmbedInstagramPostFromPostId,
  getEmbedYoutubePostFromPostId,
  getImageDimensionsFromPost,
  getInstagramLinksFromContent,
  getIYoutubeLinksFromContent,
  replaceUrlsToInternal,
} from "lib/constants";
import {
  fixInternalCdnSource,
  getExcerptFromContent,
  replaceAmpImageTags,
} from "lib/post/utils";

import { FontsCircular, Styles } from "lib/scripts";
import { NextSeo } from "next-seo";
import { useAmp } from "next/amp";
import Head from "next/head";
import { useRouter } from "next/router";
import * as React from "react";

export type Props = {
  post: Post;
  relatedPosts?: Post[] | null;
  geoIpCountry?: string | null;
  postImage?: string;
  tags?:
    | undefined
    | {
        name: string;
        count: number;
        slug: string;
      }[];
};

// @TODO - move it to server side
// eslint-disable-next-line max-statements, complexity
export default function PostPage(props: Props) {
  const isAmp = useAmp();
  const router = useRouter();
  /**
   * ClientSide Redirect Start
   */
  React.useEffect(() => {
    if (typeof props.post.postFields?.redirectUrl === "string") {
      window.location.href = props.post.postFields?.redirectUrl;
    }
  }, [props.post]);

  if (typeof props.post.postFields?.redirectUrl === "string") {
    return null; // prevent rendering original content
  }
  /**
   * ClientSide Redirect End
   */
  const hasTags = Boolean(props.post.tags?.edges.length);
  const hasCategories = Boolean(props.post.categories?.edges.length);
  const categoryColor =
    props.post.categories?.edges[0].node.categoryPageFields?.categoryColor ||
    "";
  const categoryName = props.post.categories?.edges[0].node.name || "";

  if (router.isFallback) {
    return null;
  }

  const { post, postImage, relatedPosts, geoIpCountry } = props;

  if (!post) {
    return null;
  }

  let content = fixInternalCdnSource(post.content);
  content = replaceUrlsToInternal(post.content || "");

  const embedInstagramPosts = content
    ? getInstagramLinksFromContent(content)
    : null;

  if (embedInstagramPosts) {
    // for now we don't support case if there will be non unique posts
    embedInstagramPosts.forEach((item: any) => {
      const template = isAmp
        ? getEmbedAmpInstagramPostFromPostId(item.id)
        : getEmbedInstagramPostFromPostId(item.id);

      content = content?.replace(item.url, template);
    });
  }

  const embedVideos = [];
  const embedYoutubeVideos = post.content
    ? getIYoutubeLinksFromContent(post.content)
    : null;
  if (embedYoutubeVideos) {
    embedYoutubeVideos.forEach((item) => {
      const template = isAmp
        ? getEmbedAmpYoutubePostFromPostId(item.id)
        : getEmbedYoutubePostFromPostId(item.id);

      content = content?.replace(item.url, template);
    });
  }

  const ampIframes = [];
  if (isAmp) {
    content = replaceAmpImageTags(content);

    // amp support for iframes
    content = content?.replace(
      /<iframe[^>]+><\/iframe>/g,
      function (iframeInstance: any) {
        let src = iframeInstance
          .match(/src="(.+?)"/g)
          ?.pop()
          ?.replace(/src="(.+?)"/g, "$1");

        if (src) {
          // weird case, but often for youtube
          if (src.startsWith("//")) {
            src = `https:${src}`;
          }

          ampIframes.push(src);
          return getAmpIframeFromSrc(src);
        }

        return "";
      },
    );

    // amp support for native videos
    content = content?.replace(
      /<video[^>]+><\/video>/g,
      function (videoInstance: any) {
        const src = videoInstance
          .match(/src="(.+?)"/g)
          ?.pop()
          ?.replace(/src="(.+?)"/g, "$1");

        if (src) {
          return getAmpVideoFromId(src);
        }

        return src ? getAmpVideoFromId(src) : "";
      },
    );

    // remove extra content that not supported in
    content = content?.replace(/<meta.+?>/g, "");
  }

  // sanitize styles from post content
  content = content?.replace(/style=".+?;"/g, "");

  // solution for H1 case, change in interface later
  content = content?.replace(/<h1/g, "<h2").replace(/<\/h1/g, "</h2");
  // repeat image transformation, @TODO: investigate why it doesn't apply first time
  if (!isAmp) {
    content = fixInternalCdnSource(post.content);
  }

  const { postImageWidth, postImageHeight } = getImageDimensionsFromPost(post);
  const articleProps = isAmp
    ? {
        theme: ArticleAmpPage.Theme.DATE,
        title: post.title,
        url: post.slug,
        category: post.categories?.edges?.[0]?.node?.name,
        imageUrl: postImage,
        imageHeight: postImageHeight,
        imageWidth: postImageWidth,

        content,
        relatedPosts:
          relatedPosts?.map((item) => {
            return {
              title: item.title,
              url: item.slug,
            };
          }) || null,
        geoIpCountry,
      }
    : {
        title: post.title,
        category: post?.categories?.edges?.[0]?.node?.name,
        imageUrl: postImage,

        // @todo - add name from custom field
        // authorName: post?.author?.node?.name,
        content,
        relatedPosts:
          relatedPosts?.map((item) => {
            return {
              title: item.title,
              imageAlt: item.featuredImage?.node?.altText,
              category: item?.categories?.edges?.[0]?.node?.name,
              imageUrl: item?.featuredImage?.node?.sourceUrl,

              // @todo - add name from custom field
              // authorName: item?.author?.node?.name,
              content: item.content,
              url: item.slug,
            };
          }) || null,
        geoIpCountry,
      };

  const seoBlock = (
    <NextSeo
      title={
        post.seo?.title ||
        `${CMS_NAME} - ${post.featuredPostFields?.seoTitle || post.title}`
      }
      description={
        post.seo?.metaDesc ||
        post.featuredPostFields?.seoDescription ||
        post.excerpt ||
        getExcerptFromContent(post.content) ||
        ""
      }
      noindex={typeof post.postFields?.redirectUrl === "string"}
      openGraph={{
        type: "article",
        title: `${CMS_NAME} - ${
          post.featuredPostFields?.seoTitle || post.title
        }`,
        description: post.featuredPostFields?.seoDescription || "",
        images: postImage
          ? [
              {
                url: postImage,
              },
            ]
          : undefined,
      }}
    />
  );

  return isAmp ? (
    <>
      {seoBlock}
      {isAmp ? null : <Styles />}
      {ampIframes?.length ? (
        <Head>
          <script
            async
            custom-element="amp-iframe"
            src="https://cdn.ampproject.org/v0/amp-iframe-0.1.js"
          ></script>
        </Head>
      ) : null}
      {embedYoutubeVideos?.length ? (
        <Head>
          <script
            async
            custom-element="amp-youtube"
            src="https://cdn.ampproject.org/v0/amp-youtube-0.1.js"
          ></script>
        </Head>
      ) : null}
      {embedVideos.length ? (
        <Head>
          <script
            async
            custom-element="amp-video"
            src="https://cdn.ampproject.org/v0/amp-video-0.1.js"
          ></script>
        </Head>
      ) : null}
      <ArticleAmpPage {...articleProps} />
    </>
  ) : (
    <>
      {/* potentially duplicate for ArticlePage */}
      {seoBlock}
      <Head>
        <meta itemProp="image" content={postImage} />
      </Head>
      <FontsCircular />
      <ArticlePage
        seo={{
          title: post.seo?.opengraphTitle || post.seo?.title || undefined,
          description:
            post.seo?.opengraphDescription || post.seo?.metaDesc || undefined,
          noindex: typeof post.postFields?.redirectUrl === "string",
        }}
        title={`${CMS_NAME} - ${
          post.featuredPostFields?.seoTitle || post.title
        }`}
        image={{
          src: postImage,
          // @ts-ignore
          width: postImageWidth,
          // @ts-ignore
          height: postImageHeight,
          alt: `${CMS_NAME} - ${
            post.featuredPostFields?.seoTitle || post.title
          }`,
        }}
        tags={props.tags}
      >
        <ArticleContent
          post={post}
          relatedPosts={relatedPosts}
          categoryColor={categoryColor}
          categoryName={categoryName}
          hasTags={hasTags}
          hasCategories={hasCategories}
          content={articleProps.content}
        />
      </ArticlePage>
    </>
  );
}

export { getServerSideProps } from "lib/get-server-side-props";

export const config = { amp: "hybrid" };
